import { useEffect, useRef, useState } from "react";
import api, { apiErr, downloadBlob, blobErrDetail } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import { FileSpreadsheet, Loader2, Trash2, Plus, ArrowLeft, AlertTriangle, CheckCircle2, Download, Printer } from "lucide-react";

const blank = () => ({ name: "", base_unit: "unité", units: [], duplicate_id: null, existing: null, _sel: true, _dup: "update" });

export default function CategoryImport({ open, onClose, onDone }) {
  const { current } = useActivity();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [recap, setRecap] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRows([]); setErr(""); setRecap(false);
  }, [open]);

  const normalizeRows = (list) => (list || []).map((r) => ({
    ...blank(), ...r,
    units: (r.units || []).map((u) => ({ name: u.name || "", factor: u.factor ?? 1 })),
    _sel: true, _dup: r.duplicate_id ? "update" : "create",
  }));

  const parseFile = async (f) => {
    if (!f) return;
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("activity_id", current.id);
      const { data } = await api.post("/categories/import/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const rr = normalizeRows(data.rows);
      setRows(rr);
      if (rr.length === 0) setErr("Aucune catégorie exploitable trouvée. Vérifiez les colonnes (Catégorie, Unité de base, Conditionnement, Facteur).");
    } catch (e) { setErr(apiErr(e.response?.data?.detail, e)); }
    finally { setLoading(false); }
  };

  const downloadTemplate = () => {
    const lines = [
      "Catégorie;Unité de base;Conditionnement;Facteur",
      "Boissons;casier;casier;24",
      "Boissons;casier;pack;6",
      "Ciment;sac;;",
      "Sable;m³;camion;10",
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    downloadBlob(blob, "modele-categories-biashara.csv");
  };

  const downloadSheet = async () => {
    setErr("");
    const w = window.open("", "_blank");
    try {
      const res = await api.get(`/categories/import/template.pdf?activity_id=${current.id}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (w) { w.location.href = blobUrl; }
      else { const a = document.createElement("a"); a.href = blobUrl; a.download = "fiche-categories-biashara.pdf"; a.click(); }
    } catch (e) { if (w) w.close(); setErr(apiErr(await blobErrDetail(e), e)); }
  };

  const setCell = (i, k, v) => setRows((rr) => rr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const delRow = (i) => setRows((rr) => rr.filter((_, idx) => idx !== i));
  const addRow = () => setRows((rr) => [...rr, blank()]);
  const addUnit = (i) => setRows((rr) => rr.map((r, idx) => idx === i ? { ...r, units: [...r.units, { name: "", factor: 1 }] } : r));
  const setUnit = (i, ui, k, v) => setRows((rr) => rr.map((r, idx) => idx === i ? { ...r, units: r.units.map((u, j) => j === ui ? { ...u, [k]: v } : u) } : r));
  const rmUnit = (i, ui) => setRows((rr) => rr.map((r, idx) => idx === i ? { ...r, units: r.units.filter((_, j) => j !== ui) } : r));

  const selected = rows.filter((r) => r._sel && (r.name || "").trim() && !(r.duplicate_id && r._dup === "skip"));
  const toCreate = selected.filter((r) => !r.duplicate_id).length;
  const toUpdate = selected.filter((r) => r.duplicate_id && r._dup === "update").length;

  const commit = async () => {
    setCommitting(true); setErr("");
    try {
      const payload = {
        activity_id: current.id,
        rows: selected.map((r) => ({
          name: (r.name || "").trim(),
          base_unit: (r.base_unit || "unité").trim() || "unité",
          units: (r.units || []).filter((u) => (u.name || "").trim()).map((u) => ({ name: u.name.trim(), factor: Number(u.factor || 1) })),
          action: r.duplicate_id ? (r._dup === "update" ? "update" : "create") : "create",
          existing_id: r.duplicate_id || null,
        })),
      };
      const { data } = await api.post("/categories/import/commit", payload);
      toast.success(`${data.created} catégorie(s) créée(s), ${data.updated} mise(s) à jour`);
      setRecap(false); onDone && onDone(); onClose();
    } catch (e) { setRecap(false); setErr(apiErr(e.response?.data?.detail, e)); }
    finally { setCommitting(false); }
  };

  const hasRows = rows.length > 0;

  return (
    <Modal open={open} onClose={onClose} title="Importer des catégories" wide testId="category-import-modal"
      footer={hasRows ? (
        <>
          <button className="btn-outline" onClick={() => { setRows([]); setErr(""); }} data-testid="cat-import-back-btn"><ArrowLeft className="w-4 h-4" /> Recommencer</button>
          <button className="btn-primary" onClick={() => setRecap(true)} disabled={selected.length === 0} data-testid="cat-import-review-btn">Vérifier et importer ({selected.length})</button>
        </>
      ) : <button className="btn-outline" onClick={onClose}>Fermer</button>}>

      {!hasRows && (
        <div className="space-y-3 text-center py-6" data-testid="cat-import-upload">
          <FileSpreadsheet className="w-10 h-10 mx-auto text-[#15803D]" />
          <p className="text-sm text-[#64748B]">Importez un fichier <b>.csv</b> ou <b>.xlsx</b>. Colonnes : <b>Catégorie</b>, <b>Unité de base</b>, <b>Conditionnement</b> (nom du lot), <b>Facteur</b> (taille du lot).</p>
          <p className="text-xs text-[#94A3B8]">Plusieurs conditionnements pour une même catégorie = plusieurs lignes avec le même nom de catégorie. Vous pourrez tout vérifier et corriger avant d'enregistrer.</p>
          <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="cat-import-file-input" onChange={(e) => parseFile(e.target.files?.[0])} />
          <div className="flex flex-wrap gap-2 justify-center">
            <button className="btn-primary" onClick={() => fileRef.current?.click()} disabled={loading} data-testid="cat-import-file-pick">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Choisir un fichier</button>
            <button className="btn-outline" onClick={downloadSheet} data-testid="cat-import-sheet"><Printer className="w-4 h-4" /> Ouvrir / imprimer la fiche (PDF)</button>
            <button className="btn-outline" onClick={downloadTemplate} data-testid="cat-import-template"><Download className="w-4 h-4" /> Modèle CSV</button>
          </div>
          <p className="text-xs text-[#94A3B8]">Astuce : téléchargez la fiche PDF, remplissez-la à la main, puis saisissez-la dans un fichier ou réimportez-la.</p>
          <InlineError message={err} title="Import impossible" />
        </div>
      )}

      {hasRows && (
        <div className="space-y-3">
          <div className="overflow-x-auto border border-[#E7ECF3] rounded-xl">
            <table className="w-full text-sm" data-testid="cat-import-table">
              <thead><tr>
                <th className="th !py-2 w-8"></th>
                <th className="th !py-2">Catégorie</th>
                <th className="th !py-2 w-32">Unité de base</th>
                <th className="th !py-2">Conditionnements (lots)</th>
                <th className="th !py-2 w-36">État</th>
                <th className="th !py-2 w-8"></th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="row align-top" data-testid={`cat-import-row-${i}`}>
                    <td className="td !py-1.5"><input type="checkbox" className="w-4 h-4 accent-[#2563EB]" checked={r._sel} onChange={(e) => setCell(i, "_sel", e.target.checked)} data-testid={`cat-import-select-${i}`} /></td>
                    <td className="td !py-1.5"><input className="inp !py-1.5" value={r.name} onChange={(e) => setCell(i, "name", e.target.value)} placeholder="Ex : Boissons" data-testid={`cat-import-name-${i}`} /></td>
                    <td className="td !py-1.5"><input className="inp !py-1.5" value={r.base_unit} onChange={(e) => setCell(i, "base_unit", e.target.value)} placeholder="Ex : casier, sac…" data-testid={`cat-import-baseunit-${i}`} /></td>
                    <td className="td !py-1.5">
                      <div className="space-y-1.5">
                        {r.units.map((u, ui) => (
                          <div key={ui} className="flex items-center gap-1.5">
                            <input className="inp !py-1 flex-1" value={u.name} onChange={(e) => setUnit(i, ui, "name", e.target.value)} placeholder="Lot (ex. pack)" />
                            <span className="text-[#94A3B8] text-xs">×</span>
                            <input type="number" className="inp !py-1 w-20" value={u.factor} onChange={(e) => setUnit(i, ui, "factor", e.target.value)} placeholder="24" />
                            <button onClick={() => rmUnit(i, ui)} className="text-[#B91C1C]"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        ))}
                        <button onClick={() => addUnit(i)} className="btn-ghost !py-0.5 text-xs text-[#2563EB]" data-testid={`cat-import-add-unit-${i}`}><Plus className="w-3 h-3" /> Lot</button>
                      </div>
                    </td>
                    <td className="td !py-1.5 text-xs">
                      {r.duplicate_id ? (
                        <select className="inp !py-1 text-xs" value={r._dup} onChange={(e) => setCell(i, "_dup", e.target.value)} data-testid={`cat-import-dup-${i}`}>
                          <option value="update">Existe — mettre à jour</option>
                          <option value="skip">Ignorer</option>
                        </select>
                      ) : (
                        <span className="badge-success text-[10px]"><CheckCircle2 className="w-3 h-3" /> Nouvelle</span>
                      )}
                    </td>
                    <td className="td !py-1.5"><button onClick={() => delRow(i)} className="text-[#B91C1C]" data-testid={`cat-import-del-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={addRow} className="btn-ghost !py-1 text-xs text-[#2563EB]" data-testid="cat-import-add-row"><Plus className="w-3.5 h-3.5" /> Ajouter une catégorie</button>
          {selected.some((r) => (r.units || []).some((u) => (u.name || "").trim() && !(Number(u.factor) > 0))) && (
            <div className="flex items-center gap-2 text-xs text-[#B45309]"><AlertTriangle className="w-3.5 h-3.5" /> Certains lots n'ont pas de facteur valide — ils vaudront ×1.</div>
          )}
          <InlineError message={err} title="Import impossible" />
        </div>
      )}

      <ConfirmDialog open={recap} danger={false} title="Récapitulatif de l'import" loading={committing}
        message={`À valider : ${toCreate} catégorie(s) à créer, ${toUpdate} à mettre à jour. Rien n'est enregistré tant que vous ne confirmez pas.`}
        confirmLabel="Confirmer l'import" onConfirm={commit} onClose={() => setRecap(false)} />
    </Modal>
  );
}
